import { Avatar } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { Stack } from 'components';
import { AiScribeStatus as AiScribeStatusType } from 'store/clientProfile/clientProfileSlice';
import { getNoteTypeData } from 'modules/notes/notePopup/noteEdit/fields/noteType/noteType';
import { useFlags } from 'launchdarkly-react-client-sdk';

import type { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';

import style from './note.module.scss';

type Props = {
  note: ClientProfileContentItem;
};

const iconsMap: Record<AiScribeStatusType, string> = {
  TRANSCRIBE_STARTED: 'auto_awesome',
  TRANSCRIBE_IN_PROGRESS: 'auto_awesome',
  TRANSCRIBE_COMPLETED: 'auto_awesome',
  SUMMARIZING: 'auto_awesome',
  COMPLETED: 'auto_awesome',
  FAILED: 'error_outline'
};

const statusTextMap: Record<AiScribeStatusType, string> = {
  TRANSCRIBE_STARTED: 'Processing',
  TRANSCRIBE_IN_PROGRESS: 'Processing',
  TRANSCRIBE_COMPLETED: 'Processing',
  SUMMARIZING: 'Processing',
  COMPLETED: 'Ready',
  FAILED: 'Error'
};

const AiScribeStatus = ({ status }: { status: AiScribeStatusType }) => {
  const isInProgress =
    status === 'TRANSCRIBE_STARTED' ||
    status === 'TRANSCRIBE_IN_PROGRESS' ||
    status === 'TRANSCRIBE_COMPLETED' ||
    status === 'SUMMARIZING';

  return (
    <Stack display="flex" flexDirection="row" alignItems="center" gap="4px">
      <span
        className={clsx(
          'material-icons',
          style.aiScribeStatus,
          isInProgress && style.aiScribeStatusInProgress,
          status === 'COMPLETED' && style.aiScribeStatusCompleted,
          status === 'FAILED' && style.aiScribeStatusError
        )}
      >
        {iconsMap[status]}
      </span>
      <span
        className={clsx(
          style.aiScribeStatusText,
          isInProgress && style.aiScribeStatusTextInProgress,
          status === 'COMPLETED' && style.aiScribeStatusTextCompleted,
          status === 'FAILED' && style.aiScribeStatusTextError
        )}
      >
        {statusTextMap[status]}
      </span>
    </Stack>
  );
};

const Note = ({ note }: Props) => {
  const { aiScribe } = note;
  const { clientId } = useParams();
  const { enableDateTimeUpdates } = useFlags();
  const openNoteWindow = () => {
    window.open(
      `/clients/edit/${clientId}/notes/${note.id}?context=popup`,
      String(note.id),
      `width=700,height=750,left=${window.screen.availWidth / 2 - 200},top=${
        window.screen.availHeight / 2 - 350
      }`
    );
  };

  const noteTypeData = getNoteTypeData({ canUpdateManagerReview: true });

  return (
    <div className={style.note} onClick={openNoteWindow}>
      <div className={style.noteRow}>
        <p>
          {noteTypeData.find((noteType) => noteType.id === note?.noteType)?.name ?? 'Note'}{' '}
          {note?.draft && <span className={style.draft}>Draft</span>}
        </p>
        {(note.dateOfEncounter || (enableDateTimeUpdates && note.dateAndTimeOfEncounter)) && (
          <span
            className={style.date}
            data-timestamp={
              enableDateTimeUpdates && note.dateAndTimeOfEncounter
                ? note.dateAndTimeOfEncounter
                : note?.dateOfEncounter
            }
          >
            {dayjs(
              enableDateTimeUpdates && note.dateAndTimeOfEncounter
                ? note.dateAndTimeOfEncounter
                : note?.dateOfEncounter
            ).format('MM/DD/YYYY')}
          </span>
        )}
      </div>
      <Stack display="flex" direction="row" justifyContent="space-between" alignItems="center">
        <div className={style.avatarRow}>
          <Avatar
            className={style.avatar}
            alt={note?.author?.profileImageKey}
            src={note?.author?.profileImageUrl}
          >
            <span className="material-icons-outlined">person</span>
          </Avatar>
          <div className={style.avatarName}> {note?.author?.name}</div>
        </div>
        {aiScribe && note.draft && <AiScribeStatus status={aiScribe.status} />}
      </Stack>
    </div>
  );
};

export default Note;
